import React from 'react';
import dayjs from 'dayjs';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { TWDateRangePicker } from '../../../components/ui';
import { cn } from '../../../components/ui/_cn';
import FinDropdown from './fin-dropdown';
import { PERIOD_PRESETS, stepPeriod } from './connect-period';

// What the Custom option reads as. The arrows always resolve to a custom
// window (stepPeriod's own comment explains why), so a restaurant that
// pressed "back" from This week used to watch the control answer "CUSTOM" —
// a true statement about our data model and a useless one about their week.
// Naming the window instead means the control says where they actually are.
export const customLabel = (custom) => {
  const from = custom?.from ? dayjs(custom.from) : null;
  const to = custom?.to ? dayjs(custom.to) : null;
  if (!from || !to) return 'Custom';
  const sameYear = from.year() === to.year();
  const fmt = sameYear ? 'MMM D' : 'MMM D, YYYY';
  return `${from.format(fmt)} – ${to.format(sameYear ? 'MMM D, YYYY' : fmt)}`;
};

const arrowCls =
  'inline-flex items-center justify-center px-[11px] py-[9px] text-fin-ink-2 hover:bg-fin-recess';

// One bordered cluster — ‹ preset ›— the mock's segmented stepper (spec
// 2026-09-21 §4.6). The group carries the border; the centre is a bare
// FinDropdown. The arrows stay because they do something a list cannot:
// shift the window on screen by its own length, so "the week before this
// one" is one press rather than a calendar.
//
// The component owns no state — the container keeps {preset, custom} so a
// refetch can read them. Mounted by Order Reports and by the admin
// Settlement screen alike.
const ConnectPeriodPicker = ({ preset, custom, onChange }) => {
  const range = [custom?.from || null, custom?.to || null];
  const options = PERIOD_PRESETS.map((option) =>
    option.value === 'custom' ? { ...option, label: customLabel(custom) } : option,
  );

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="inline-flex overflow-hidden rounded-lg border border-fin-border-2 bg-fin-surface">
        <button
          type="button"
          className={cn(arrowCls, 'border-r border-fin-border')}
          aria-label="Previous period"
          onClick={() => onChange(stepPeriod(preset, custom, -1))}
        >
          <ChevronLeftIcon className="h-3 w-3 stroke-[2.5]" aria-hidden="true" />
        </button>
        <FinDropdown
          mode="radio"
          size="md"
          bare
          aria-label="Period"
          value={preset}
          options={options}
          onChange={(next) => {
            // Re-choosing the already-active preset is a no-op: the period
            // hasn't changed, so it must not trigger a refetch.
            if (next === preset) return;
            onChange({ preset: next, custom: custom || { from: null, to: null } });
          }}
        />
        <button
          type="button"
          className={cn(arrowCls, 'border-l border-fin-border')}
          aria-label="Next period"
          onClick={() => onChange(stepPeriod(preset, custom, 1))}
        >
          <ChevronRightIcon className="h-3 w-3 stroke-[2.5]" aria-hidden="true" />
        </button>
      </div>
      {preset === 'custom' ? (
        // antd's RangePicker doesn't forward aria-label to its inputs, so
        // the accessible name is given on a wrapping group instead.
        <div role="group" aria-label="Custom date range">
          <TWDateRangePicker
            size="middle"
            wrapperClassName="sm:w-72"
            value={range}
            allowClear
            onChange={(dates) =>
              onChange({ preset: 'custom', custom: { from: dates?.[0] || null, to: dates?.[1] || null } })
            }
          />
        </div>
      ) : null}
    </div>
  );
};

export default ConnectPeriodPicker;
